<template>
  <b-card>
    <h3 class="mb-4">Earnings Report</h3>
    <b-row class="mb-4 text-center">
      <b-col lg="10"></b-col>
      <!-- Export Button -->
      <b-col lg="2" class="d-flex justify-content-end">
        <b-button @click="exportAllDataToCSV" variant="primary">Export</b-button>
      </b-col>
    </b-row>
    <!-- Summary Boxes -->
    <b-row class="mb-4 text-center">
      <!-- Total Earnings -->
      <b-col lg="4">
        <b-card
          class="shadow-sm border-0"
          style="background: linear-gradient(135deg, #28a745, #5cd17d); color: white;"
        >
          <div class="d-flex flex-column align-items-center">
            <b-icon icon="currency-dollar" font-scale="2" class="mb-2"></b-icon>
            <h6 class="mb-1 text-uppercase">Total Earnings</h6>
            <h2 class="font-weight-bold">{{ formatCurrency(summary.total_earnings) }}</h2>
          </div>
        </b-card>
      </b-col>

      <!-- Total Expenses -->
      <b-col lg="4">
        <b-card
          class="shadow-sm border-0"
          style="background: linear-gradient(135deg, #dc3545, #f36c6c); color: white;"
        >
          <div class="d-flex flex-column align-items-center">
            <b-icon icon="credit-card" font-scale="2" class="mb-2"></b-icon>
            <h6 class="mb-1 text-uppercase">Total Expenses</h6>
            <h2 class="font-weight-bold">{{ formatCurrency(summary.total_expenses) }}</h2>
          </div>
        </b-card>
      </b-col>

      <!-- Profit -->
      <b-col lg="4">
        <b-card
          class="shadow-sm border-0"
          style="background: linear-gradient(135deg, #007bff, #5aa0f2); color: white;"
        >
          <div class="d-flex flex-column align-items-center">
            <b-icon icon="graph-up" font-scale="2" class="mb-2"></b-icon>
            <h6 class="mb-1 text-uppercase">Profit</h6>
            <h2 class="font-weight-bold">{{ formatCurrency(summary.profit) }}</h2>
          </div>
        </b-card>
      </b-col>
    </b-row>

    <!-- Filters -->
    <b-row class="align-items-center mb-3">
      <!-- Search -->
      <b-col lg="4">
        <b-input-group size="sm">
          <b-form-input
            v-model="filter"
            type="search"
            placeholder="Search..."
            @keyup.enter="applyFilter"
          />
          <b-input-group-append>
            <b-button @click="applyFilter" :disabled="!filter" variant="primary">Apply</b-button>
            <b-button @click="clearFilter" :disabled="!filter">Clear</b-button>
          </b-input-group-append>
        </b-input-group>
      </b-col>

      <!-- Date Filters -->
      <b-col lg="3">
        <b-form-input v-model="startDateFilter" type="date" />
      </b-col>
      <b-col lg="3">
        <b-form-input v-model="endDateFilter" type="date" @change="fetchEarnings" />
      </b-col>

      <!-- Dropdown Filter -->
      <b-col lg="2">
        <b-form-select v-model="selectedFilter" :options="filterOptions" @change="fetchEarnings" />
      </b-col>
    </b-row>

    <!-- Loading -->
    <div v-if="loading" class="text-center mt-4">
      <b-spinner label="Loading..."></b-spinner>
    </div>

    <!-- Table -->
    <b-row>
      <div class="col-12 mt-2">
        <b-skeleton-table
                    v-if="loading"
                    :rows="perPage"
                    :columns="fields.length"
                    animation="wave"
                    bordered
                    class="mb-3"
                    />
        <b-table v-else
          id="dataTable"
          :items="earnings"
          :busy.sync="loading"
          :fields="fields"
          responsive
          show-empty
          striped hover  class="custom-zebra"
        >
          <template #cell(date)="row">
            <span>{{ formatDate(row.item.date) }}</span>
          </template>
          <template #cell(amount)="row">
            <span>{{ formatCurrency(row.item.amount) }}</span>
          </template>
          <template #cell(user)="row">
            <span>
              {{
                row.item.user
                  ? row.item.user.first_name + ' ' + row.item.user.last_name
                  : row.item.customer_name
              }}
            </span>
          </template>
        </b-table>

        <!-- Pagination -->
        <div class="mt-3 d-flex justify-content-end">
          <b-pagination
            v-model="currentPage"
            :total-rows="total"
            :per-page="perPage"
            aria-controls="dataTable"
            @change="pageChanged"
          />
        </div>
      </div>
    </b-row>
  </b-card>
</template>

<script>
import {
  BRow,
  BCol,
  BCard,
  BButton,
  BTable,
  BFormInput,
  BPagination,
  BInputGroup,
  BInputGroupAppend,
  BSpinner,
  BFormSelect,
  BSkeleton,
  BSkeletonTable,
} from "bootstrap-vue";
import axios from "../../../axios";
import Papa from "papaparse"; // <-- make sure you import this

export default {
  data() {
    return {
      filter: "",
      total: 0,
      perPage: 20,
      currentPage: 1,
      earnings: [],
      loading: false,
      startDateFilter: "",
      endDateFilter: "",
      selectedFilter: "",
      filterOptions: [
        { value: "", text: "Select Filter" },
        { value: "daily", text: "Daily" },
        { value: "weekly", text: "Weekly" },
        { value: "monthly", text: "Monthly" },
        { value: "yearly", text: "Yearly" },
      ],
      summary: {
        total_earnings: 0,
        total_expenses: 0,
        profit: 0,
      },
      fields: [
        { key: "id", label: "ID", sortable: true },
        { key: "user", label: "User Name" },
        { key: "amount", label: "Amount($)", sortable: true },
        { key: "service_type", label: "Service Type", sortable: true },
        { key: "date", label: "Date", sortable: true },
      ],
    };
  },
  components: {
    BRow,
    BCol,
    BCard,
    BButton,
    BTable,
    BFormInput,
    BPagination,
    BInputGroup,
    BInputGroupAppend,
    BSpinner,
    BFormSelect,
    BSkeleton,
    BSkeletonTable,
  },
  mounted() {
    this.fetchEarnings();
  },
  methods: {
    async fetchEarnings() {
      this.loading = true;
      try {
        const response = await axios.post("earningsReport", {
          page: this.currentPage,
          per_page: this.perPage,
          search: this.filter,
          selected_date: this.startDateFilter,
          ended_date: this.endDateFilter,
          filter: this.selectedFilter,
        });

        this.earnings = Array.isArray(response.data.data) ? response.data.data : [];
        this.total = response.data.total || 0;
        this.perPage = response.data.per_page;
        this.currentPage = response.data.current_page;

        // Summary from API
        this.summary.total_earnings = response.data.total_earnings ?? 0;
        this.summary.total_expenses = response.data.total_expenses ?? 0;
        this.summary.profit = response.data.profit ?? 0;
      } catch (error) {
        this.earnings = [];
      } finally {
        this.loading = false;
      }
    },
    pageChanged(newPage) {
      this.currentPage = newPage;
      this.fetchEarnings();
    },
    applyFilter() {
      this.currentPage = 1;
      this.fetchEarnings();
    },
    clearFilter() {
      this.filter = "";
      this.currentPage = 1;
      this.fetchEarnings();
    },
    formatDate(dateString) {
      if (!dateString) return "";
      return new Date(dateString).toISOString().split("T")[0];
    },
    exportAllDataToCSV() {
      axios
        .get("earnings/export")
        .then((response) => {
          const csv = Papa.unparse(response.data);
          const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
          const link = document.createElement("a");
          link.href = URL.createObjectURL(blob);
          link.setAttribute("download", "earnings.csv");
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        })
        .catch(() => {});
    },
    formatCurrency(value) {
      if (!value) return "$0.00";
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
      }).format(value);
    },
  },
};
</script>
  <style>
  /* Zebra striping */
  .custom-zebra tbody tr:nth-of-type(odd) {
    background-color: #f0f2f5; /* darker than default */
  }

  /* Hover effect */
  .custom-zebra tbody tr:hover {
    background-color: #e0e4ea !important; /* slightly darker hover */
  }
  </style>